import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { Combobox } from '@/components/ui/combobox';
import { FloatingField } from '@/components/ui/floating-field';
import { MoneyField } from '@/components/ui/money-field';
import { DateField } from '@/components/ui/date-field';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, apiForm } from '@/lib/api';

function toYmd(d) {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  // Prefer local calendar date to avoid UTC day-shift
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Prefer explicit rateDollars; treat large integers as cents. */
function rateToDollarsInput(f) {
  if (!f) return '';
  if (f.rateDollars != null && f.rateDollars !== '') {
    const n = Number(f.rateDollars);
    if (Number.isFinite(n) && n > 0) return String(n);
  }
  if (typeof f.rate === 'number' && f.rate > 0) {
    // preview stores cents when rateDollars is also set; alone, values >= 10k are cents
    if (f.rateDollars == null && f.rate >= 10000) return String(f.rate / 100);
    if (f.rateDollars == null && f.rate < 10000) return String(f.rate);
  }
  return '';
}

function formatRateDisplay(f) {
  const dollars = rateToDollarsInput(f);
  if (!dollars) return null;
  const n = Number(dollars);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : `$${dollars}`;
}

function fieldLine(label, value, { missing = false } = {}) {
  const empty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
  const display = empty
    ? '—'
    : Array.isArray(value)
      ? value.join(', ')
      : String(value);
  return (
    <div className="flex gap-2 text-sm py-0.5 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className={`font-medium break-words ${empty && missing ? 'text-amber-700' : ''}`}>
        {display}
      </span>
    </div>
  );
}

function matchParty(parties, name) {
  if (!name) return '';
  const n = String(name).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (n.length < 3) return '';
  const hit = parties.find((p) => {
    const pn = String(p.name ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return pn && (pn.includes(n) || n.includes(pn));
  });
  return hit?.id ?? '';
}

export default function AIPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pdfInputRef = useRef(null);
  const reviewSectionRef = useRef(null);
  const hydrateKeyRef = useRef('');
  const [text, setText] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [extractingText, setExtractingText] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [loadNumber, setLoadNumber] = useState('');
  const [broker, setBroker] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shipperName, setShipperName] = useState('');
  const [consigneeName, setConsigneeName] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [rateDollars, setRateDollars] = useState('');
  const [pickupNumbers, setPickupNumbers] = useState('');
  const [pickupDate, setPickupDate] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [shipperId, setShipperId] = useState('');
  const [consigneeId, setConsigneeId] = useState('');

  const busy = uploadingPdf || extractingText || approving;

  const scrollReviewIntoView = () => {
    // Double rAF: wait until selected review panel has painted in the main scroller
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reviewSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const selectForReview = (id) => {
    if (!id) {
      setSelectedId('');
      return;
    }
    setSelectedId(id);
    scrollReviewIntoView();
  };

  const dataQ = useQuery({
    queryKey: ['ai-page'],
    queryFn: async () => {
      const [ing, p, st] = await Promise.all([
        api('/api/v1/ai/ingestions'),
        api('/api/v1/shippers'),
        api('/api/v1/ai/status'),
      ]);
      if (ing.status !== 200) throw new Error(ing.data?.error ?? 'Failed to load ingestions');
      if (p.status !== 200) throw new Error(p.data?.error ?? 'Failed to load parties');
      return {
        ingestions: ing.data.ingestions ?? [],
        parties: p.data.shippers ?? [],
        openaiConfigured: st.status === 200 ? !!st.data.openaiConfigured : true,
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load AI page');
  }, [dataQ.isError, dataQ.error]);

  const ingestions = dataQ.data?.ingestions ?? [];
  const parties = dataQ.data?.parties ?? [];
  const openaiConfigured = dataQ.data?.openaiConfigured ?? true;
  const selected = ingestions.find((i) => i.id === selectedId);
  const extracted = selected?.extractedFields ?? null;
  const hydrateKey = selected
    ? `${selected.id}|${selected.status}|${extracted?.loadNumber ?? ''}|${extracted?.rateDollars ?? extracted?.rate ?? ''}|${extracted?.origin ?? ''}`
    : '';

  useEffect(() => {
    if (!selectedId) {
      hydrateKeyRef.current = '';
      setLoadNumber('');
      setBroker('');
      setCarrier('');
      setShipperName('');
      setConsigneeName('');
      setOrigin('');
      setDestination('');
      setRateDollars('');
      setPickupNumbers('');
      setPickupDate(null);
      setDeliveryDate(null);
      setShipperId('');
      setConsigneeId('');
      return;
    }
    // Avoid re-writing the form on every list refetch (prevents edit loops)
    if (hydrateKeyRef.current === hydrateKey) return;
    hydrateKeyRef.current = hydrateKey;

    const ing = ingestions.find((i) => i.id === selectedId);
    const f = ing?.extractedFields;
    if (!f) return;

    setLoadNumber(String(f.loadNumber ?? ''));
    setBroker(String(f.broker ?? ''));
    setCarrier(String(f.carrier ?? ''));
    setShipperName(String(f.shipper ?? f.shipperName ?? ''));
    setConsigneeName(String(f.consignee ?? f.consigneeName ?? ''));
    setOrigin(String(f.origin ?? ''));
    setDestination(String(f.destination ?? ''));
    setRateDollars(rateToDollarsInput(f));
    setPickupNumbers(
      Array.isArray(f.pickupNumbers)
        ? f.pickupNumbers.join(', ')
        : String(f.pickupNumbers ?? '')
    );
    const pu = String(f.pickupDate ?? '').slice(0, 10);
    const del = String(f.deliveryDate ?? '').slice(0, 10);
    setPickupDate(pu ? new Date(`${pu}T12:00:00`) : null);
    setDeliveryDate(del ? new Date(`${del}T12:00:00`) : null);
    setShipperId(matchParty(parties, f.shipper ?? f.shipperName));
    setConsigneeId(matchParty(parties, f.consignee ?? f.consigneeName));
  }, [selectedId, hydrateKey, ingestions, parties]);

  const extract = async () => {
    if (busy) return;
    if (!text.trim()) {
      toast.error('Paste rate confirmation text first');
      return;
    }
    setExtractingText(true);
    try {
      const r = await api('/api/v1/ai/extract', { method: 'POST', body: { text }, timeoutMs: 90000 });
      if (r.status >= 200 && r.status < 300) {
        toast.success('Extracted');
        await qc.invalidateQueries({ queryKey: ['ai-page'] });
        const id = r.data?.ingestion?.id;
        if (id) {
          hydrateKeyRef.current = '';
          selectForReview(id);
        }
      } else {
        toast.error(String(r.data?.error ?? 'Extract failed'));
      }
    } catch (err) {
      toast.error(err?.message ?? 'Extract failed');
    } finally {
      setExtractingText(false);
    }
  };

  const uploadPdf = async () => {
    if (busy) return;
    if (!pdfFile) {
      toast.error('Choose a PDF rate confirmation');
      return;
    }
    if (!/\.pdf$/i.test(pdfFile.name) && pdfFile.type !== 'application/pdf') {
      toast.error('Only PDF files are supported');
      return;
    }
    setUploadingPdf(true);
    try {
      const contentBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const b64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(b64);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read PDF'));
        reader.readAsDataURL(pdfFile);
      });
      let r = await api('/api/v1/ai/rate-con/upload-json', {
        method: 'POST',
        body: { fileName: pdfFile.name, contentBase64 },
        timeoutMs: 120000,
      });
      if (r.status === 404 || r.status === 405) {
        const fd = new FormData();
        fd.append('file', pdfFile);
        r = await apiForm('/api/v1/ai/rate-con/upload', fd, { timeoutMs: 120000 });
      }
      if (r.status >= 200 && r.status < 300) {
        toast.success('PDF extracted');
        setPdfFile(null);
        if (pdfInputRef.current) pdfInputRef.current.value = '';
        await qc.invalidateQueries({ queryKey: ['ai-page'] });
        const id = r.data?.ingestion?.id;
        if (id) {
          hydrateKeyRef.current = '';
          selectForReview(id);
        }
      } else {
        toast.error(String(r.data?.error ?? r.data?.message ?? `PDF upload failed (${r.status})`));
      }
    } catch (err) {
      toast.error(err?.message ?? 'PDF upload failed');
    } finally {
      setUploadingPdf(false);
    }
  };

  const approve = async () => {
    if (busy) return;
    if (!selectedId) {
      toast.error('Select an ingestion');
      return;
    }
    if (!loadNumber.trim()) {
      toast.error('Load number is required');
      return;
    }
    if (!shipperId || !consigneeId) {
      toast.error('Select shipper and consignee parties');
      return;
    }
    if (!rateDollars || Number(rateDollars) <= 0) {
      toast.error('Rate must be greater than 0');
      return;
    }
    setApproving(true);
    try {
      const r = await api(`/api/v1/ai/ingestions/${selectedId}/approve`, {
        method: 'POST',
        body: {
          loadNumber: loadNumber.trim(),
          rateDollars: Number(rateDollars),
          pickupDate: toYmd(pickupDate) || undefined,
          deliveryDate: toYmd(deliveryDate) || undefined,
          shipperId,
          consigneeId,
          origin: origin.trim() || undefined,
          destination: destination.trim() || undefined,
        },
      });
      if (r.status === 201) {
        const load = r.data?.load;
        if (load?.id) {
          // Smooth board update — show in Loads immediately
          qc.setQueryData(['loads'], (prev) => {
            if (!Array.isArray(prev)) return [load];
            if (prev.some((l) => l.id === load.id)) {
              return prev.map((l) => (l.id === load.id ? load : l));
            }
            return [load, ...prev];
          });
          qc.setQueryData(['dispatch-board'], (prev) => {
            if (!prev || typeof prev !== 'object') return prev;
            const rows = Array.isArray(prev.loads) ? prev.loads : [];
            const nextLoads = rows.some((l) => l.id === load.id)
              ? rows.map((l) => (l.id === load.id ? load : l))
              : [load, ...rows];
            return { ...prev, loads: nextLoads };
          });
        }
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['ai-page'] }),
          qc.invalidateQueries({ queryKey: ['loads'] }),
          qc.invalidateQueries({ queryKey: ['dispatch-board'] }),
        ]);
        toast.success(`Load ${load?.loadNumber ?? loadNumber} created`);
        if (load?.id) {
          navigate(`/loads?highlight=${encodeURIComponent(load.id)}`);
        }
      } else {
        toast.error(String(r.data?.error ?? 'Approve failed'));
      }
    } catch (err) {
      toast.error(err?.message ?? 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const missingCore =
    extracted &&
    [
      !extracted.loadNumber && 'Load #',
      !(extracted.shipper || extracted.shipperName) && 'Shipper',
      !(extracted.consignee || extracted.consigneeName) && 'Consignee',
      !extracted.origin && 'Origin',
      !extracted.destination && 'Destination',
      extracted.rateDollars == null && extracted.rate == null && 'Rate',
    ].filter(Boolean);

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Rate Confirmation Import"
        description="Core fields are extracted when found. Optional fields stay blank unless the system is confident."
        icon="Sparkles"
        metrics={[
          { label: 'Ingestions', value: ingestions.length },
          {
            label: 'Pending',
            value: ingestions.filter((i) => i.status === 'extracted').length,
          },
        ]}
      />

      {!openaiConfigured && (
        <Card className="border-amber-500/40">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            OpenAI is not configured — scanned/image PDFs may need it. Embedded-text rate cons
            (Best Bay, Echo, Axle-style) still extract with local rules.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Manual import</CardTitle>
            <CardDescription>
              Upload a carrier rate confirmation PDF or paste the text
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                PDF upload
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  id="ai-rate-con-pdf"
                  data-testid="ai-rate-con-pdf-input"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                />
                <label
                  htmlFor="ai-rate-con-pdf"
                  className="flex-1 flex items-center gap-2 h-10 px-3 border rounded-md cursor-pointer hover:bg-accent text-sm"
                >
                  <Icons.FileUp className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {pdfFile ? pdfFile.name : 'Choose rate confirmation PDF…'}
                  </span>
                </label>
              </div>
              <Button
                className="w-full"
                onClick={uploadPdf}
                disabled={busy || !pdfFile}
                data-testid="ai-rate-con-pdf-upload"
              >
                {uploadingPdf ? (
                  <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Icons.Upload className="h-4 w-4 mr-2" />
                )}
                {uploadingPdf ? 'Extracting PDF…' : 'Upload & Extract PDF'}
              </Button>
            </div>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or paste text</span>
              </div>
            </div>

            <textarea
              className="w-full min-h-[120px] p-3 border rounded-md text-sm resize-none bg-background"
              placeholder="Paste rate confirmation email text here..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="ai-extract-text"
            />
            <Button
              className="w-full"
              variant="outline"
              onClick={extract}
              disabled={busy || !text.trim()}
              data-testid="ai-extract-text-btn"
            >
              {extractingText ? (
                <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Icons.Sparkles className="h-4 w-4 mr-2" />
              )}
              {extractingText ? 'Extracting…' : 'Extract from text'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent imports</CardTitle>
            <CardDescription>Select one to review mapped fields</CardDescription>
          </CardHeader>
          <CardContent>
            {ingestions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {dataQ.isLoading ? 'Loading…' : 'No imports yet — upload a rate confirmation PDF'}
              </p>
            ) : (
              <div className="space-y-3">
                {ingestions.slice(0, 12).map((ing) => {
                  const f = ing.extractedFields ?? {};
                  const title = f.loadNumber || ing.id.slice(0, 8);
                  const sub = [f.broker, f.shipper || f.shipperName, f.origin, f.destination]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div
                      key={ing.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        selectedId === ing.id ? 'border-primary bg-muted/40' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Icons.FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm font-mono truncate">{title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {sub ||
                              (ing.createdAt ? new Date(ing.createdAt).toLocaleString() : '')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusPill status={ing.status} />
                        <Button size="sm" variant="outline" onClick={() => selectForReview(ing.id)}>
                          Review
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div
        ref={reviewSectionRef}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Extracted data</CardTitle>
            <CardDescription>
              Core fields must be solid for approve. Optionals stay blank when unclear.
              {missingCore?.length ? (
                <span className="block text-amber-700 mt-1">
                  Core missing: {missingCore.join(', ')} — fill in on the right before approve
                </span>
              ) : null}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!selected ? (
              <p className="text-sm text-muted-foreground">Select an import to see extracted fields</p>
            ) : !extracted ? (
              <p className="text-sm text-muted-foreground">No fields on this ingestion yet</p>
            ) : (
              <>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Core
                  </p>
                  <div className="space-y-1">
                    {fieldLine('Load #', extracted.loadNumber, { missing: true })}
                    {fieldLine('Shipper', extracted.shipper ?? extracted.shipperName, {
                      missing: true,
                    })}
                    {fieldLine('Consignee', extracted.consignee ?? extracted.consigneeName, {
                      missing: true,
                    })}
                    {fieldLine('Origin', extracted.origin, { missing: true })}
                    {fieldLine('Destination', extracted.destination, { missing: true })}
                    {fieldLine('Rate', formatRateDisplay(extracted), { missing: true })}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Optional
                  </p>
                  <div className="space-y-1">
                    {fieldLine('Broker', extracted.broker)}
                    {fieldLine('Carrier', extracted.carrier)}
                    {fieldLine('Pickup #', extracted.pickupNumbers)}
                    {fieldLine('Pickup date', extracted.pickupDate)}
                    {fieldLine('Delivery date', extracted.deliveryDate)}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Review / Approve</CardTitle>
            <CardDescription>
              Confirm core fields and parties. Leave optionals blank if the RC did not provide them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Ingestion
              </label>
              <Combobox
                options={ingestions.map((i) => ({
                  value: i.id,
                  label: `${i.extractedFields?.loadNumber ?? i.id.slice(0, 8)} · ${i.status}`,
                }))}
                value={selectedId}
                onChange={selectForReview}
                placeholder="Select ingestion"
              />
            </div>
            {selected ? (
              <p className="text-sm">
                Status: <StatusPill status={selected.status} />
              </p>
            ) : null}

            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-1">
              Core
            </p>
            <FloatingField
              label="Load number"
              value={loadNumber}
              onChange={(e) => setLoadNumber(e.target.value)}
              data-testid="ai-review-load-number"
            />
            <MoneyField
              label="Rate (USD)"
              value={rateDollars}
              onChange={(e) => setRateDollars(e.target.value)}
            />
            <FloatingField
              label="Shipper name (from RC)"
              value={shipperName}
              onChange={(e) => setShipperName(e.target.value)}
            />
            <FloatingField
              label="Consignee name (from RC)"
              value={consigneeName}
              onChange={(e) => setConsigneeName(e.target.value)}
            />
            <FloatingField
              label="Origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              data-testid="ai-review-origin"
            />
            <FloatingField
              label="Destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              data-testid="ai-review-destination"
            />

            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">
              Optional
            </p>
            <FloatingField
              label="Broker"
              value={broker}
              onChange={(e) => setBroker(e.target.value)}
            />
            <FloatingField
              label="Carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
            />
            <FloatingField
              label="Pickup numbers"
              value={pickupNumbers}
              onChange={(e) => setPickupNumbers(e.target.value)}
            />
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Pickup date
              </label>
              <DateField
                value={pickupDate}
                onChange={(d) => {
                  setPickupDate(d);
                  if (deliveryDate && d && new Date(deliveryDate) < new Date(d)) {
                    setDeliveryDate(d);
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Delivery date
              </label>
              <DateField
                value={deliveryDate}
                onChange={setDeliveryDate}
                minDate={pickupDate || undefined}
              />
            </div>

            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">
              Parties (required to create load)
            </p>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Shipper party
              </label>
              {shipperName ? (
                <p className="text-xs text-muted-foreground">From rate con: {shipperName}</p>
              ) : null}
              <Combobox
                options={parties.map((p) => ({ value: p.id, label: p.name }))}
                value={shipperId}
                onChange={setShipperId}
                placeholder="Select shipper party"
                allowClear
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Consignee party
              </label>
              {consigneeName ? (
                <p className="text-xs text-muted-foreground">From rate con: {consigneeName}</p>
              ) : null}
              <Combobox
                options={parties.map((p) => ({ value: p.id, label: p.name }))}
                value={consigneeId}
                onChange={setConsigneeId}
                placeholder="Select consignee party"
                allowClear
              />
            </div>

            <Button
              className="w-full"
              onClick={() => {
                if (busy) return;
                if (!selectedId) {
                  toast.error('Select an ingestion');
                  return;
                }
                if (!loadNumber.trim()) {
                  toast.error('Load number is required');
                  return;
                }
                if (!shipperId || !consigneeId) {
                  toast.error('Select shipper and consignee parties');
                  return;
                }
                if (!rateDollars || Number(rateDollars) <= 0) {
                  toast.error('Rate must be greater than 0');
                  return;
                }
                setApproveOpen(true);
              }}
              disabled={busy || !selected || selected.status !== 'extracted'}
              data-testid="ai-approve-btn"
            >
              {approving ? (
                <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {approving ? 'Creating load…' : 'Approve → Create Load'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Approve rate con?"
        description="This will create a load from the reviewed extraction. Continue?"
        confirmLabel={approving ? 'Creating…' : 'Approve'}
        onConfirm={() => void approve()}
      />
    </div>
  );
}
